import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../common/LoadingSpinner';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import ImageUpload from '../common/ImageUpload';
import instituteService from '../../services/instituteService';
import { imageService } from '../../services/imageService';
import { societyService, extractSocietyResults } from '../../services/societyService';
import { imageFieldUrl } from '../../utils/imageFallback';

const InstituteCreateForm = ({ isEditMode = false, instituteId = null, initialInstitute = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [societies, setSocieties] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [displayImageIndex, setDisplayImageIndex] = useState(null);
  const [imagesToRemove, setImagesToRemove] = useState([]);

  const societyFromUrl = searchParams.get('society');

  const [formData, setFormData] = useState({
    name: '',
    type: 'school',
    description: '',
    address: '',
    city: '',
    contact_phone: '',
    contact_email: '',
    website: '',
    established_year: '',
    principal_name: '',
    total_students: '',
    facilities: '',
    admission_info: '',
    society: societyFromUrl || '',
    is_public: false,
  });

  useEffect(() => {
    if (!isEditMode || !initialInstitute) return;
    const societyId = initialInstitute.society?.id ?? initialInstitute.society;
    setFormData({
      name: initialInstitute.name ?? '',
      type: initialInstitute.institute_type ?? 'school',
      description: initialInstitute.description ?? '',
      address: initialInstitute.address ?? '',
      city: initialInstitute.city ?? '',
      contact_phone: initialInstitute.phone ?? '',
      contact_email: initialInstitute.contact_email ?? '',
      website: initialInstitute.website ?? '',
      established_year: initialInstitute.established_year ?? '',
      total_students: initialInstitute.total_students ?? '',
      principal_name: initialInstitute.principal_name ?? '',
      facilities: initialInstitute.facilities ?? '',
      admission_info: initialInstitute.admission_info ?? '',
      society: societyId != null ? String(societyId) : '',
      is_public: !!initialInstitute.is_public,
    });
    if (initialInstitute.images?.length) {
      const coverId =
        initialInstitute.display_image?.id ??
        initialInstitute.images.find((img) => img.is_featured)?.id;
      if (coverId != null) {
        const idx = initialInstitute.images.findIndex((img) => img.id === coverId);
        if (idx >= 0) setDisplayImageIndex(idx);
      }
    }
  }, [isEditMode, initialInstitute]);

  const existingImages =
    isEditMode && initialInstitute?.images?.length
      ? initialInstitute.images
          .map((img) => {
            const url = imageFieldUrl(img.original_image);
            return url && img.id != null ? { id: img.id, url } : null;
          })
          .filter(Boolean)
      : [];
  const keptExistingImages = existingImages.filter((img) => !imagesToRemove.includes(img.id));

  const remapDisplayIndexAfterRemove = (removedGlobalIndex) => {
    setDisplayImageIndex((prev) => {
      if (prev === null) return null;
      const remainingCount = keptExistingImages.length - 1 + selectedImages.length;
      if (prev === removedGlobalIndex) {
        return remainingCount > 0 ? 0 : null;
      }
      if (prev > removedGlobalIndex) return prev - 1;
      return prev;
    });
  };

  const typeOptions = [
    { value: 'school', label: 'School' },
    { value: 'college', label: 'College' },
    { value: 'university', label: 'University' },
    { value: 'training', label: 'Training Center' },
    { value: 'tuition', label: 'Tuition Center' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);
        const response = await societyService.getSocieties({ page_size: 500 });
        const list = extractSocietyResults(response);
        setSocieties(list);

        if (!isEditMode) {
          if (societyFromUrl) {
            const sid = parseInt(societyFromUrl, 10);
            const match = list.find((s) => s.id === sid);
            if (match) {
              setFormData((prev) => ({ ...prev, society: String(match.id) }));
            }
          } else if (user?.society?.id) {
            setFormData((prev) => ({ ...prev, society: String(user.society.id) }));
          } else if (list.length === 1) {
            setFormData((prev) => ({ ...prev, society: String(list[0].id) }));
          }
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        showError('Failed to load societies. Please try again.');
      } finally {
        setLoadingSocieties(false);
      }
    };

    if (isAuthenticated) {
      loadSocieties();
    }
  }, [isAuthenticated, societyFromUrl, user, showError, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const navigateBack = () => {
    if (isEditMode && instituteId) {
      navigate(`/institutes/${instituteId}`);
      return;
    }
    const isSocietyContext = location.pathname.includes('/societies/');
    if (isSocietyContext) {
      const pathParts = location.pathname.split('/');
      const societySlugIndex = pathParts.indexOf('societies') + 1;
      const societySlug = pathParts[societySlugIndex];
      navigate(`/societies/${societySlug}/institutes`);
    } else {
      navigate('/institutes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      showError(isEditMode ? 'Please log in to edit this institute' : 'Please log in to add an institute');
      return;
    }

    if (!formData.society) {
      showError('Please select a society');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        institute_type: formData.type,
        description: formData.description.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        phone: formData.contact_phone.trim(),
        contact_email: formData.contact_email.trim(),
        website: formData.website.trim(),
        society: formData.society,
        is_public: formData.is_public,
        facilities: formData.facilities.trim(),
        admission_info: formData.admission_info.trim(),
        principal_name: formData.principal_name.trim(),
        images: selectedImages,
      };

      if (formData.established_year !== '') {
        payload.established_year = formData.established_year;
      }
      if (formData.total_students !== '') {
        payload.total_students = formData.total_students;
      }
      const totalImages = keptExistingImages.length + selectedImages.length;
      let coverIndex = displayImageIndex;
      if (totalImages > 0 && coverIndex === null) {
        coverIndex = 0;
      }
      if (coverIndex !== null && coverIndex >= totalImages) {
        coverIndex = 0;
      }
      if (coverIndex !== null) {
        payload.display_image_index = coverIndex;
      }

      if (isEditMode && instituteId) {
        for (const imageId of imagesToRemove) {
          await imageService.deleteImage(imageId);
        }
        await instituteService.updateInstitute(instituteId, payload);
        showSuccess('Institute updated successfully!');
      } else {
        await instituteService.createInstitute(payload);
        showSuccess('Institute added successfully!');
      }
      navigateBack();
    } catch (error) {
      console.error(isEditMode ? 'Error updating institute:' : 'Error adding institute:', error);
      const message =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        error.message ||
        (isEditMode ? 'Failed to update institute. Please try again.' : 'Failed to add institute. Please try again.');
      showError(typeof message === 'string' ? message : (isEditMode ? 'Failed to update institute.' : 'Failed to add institute.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">
            {isEditMode ? 'Please log in to edit this institute.' : 'Please log in to add an institute.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Edit Institute' : 'Add Institute'}</h2>
        <p className="text-gray-600 mt-1">
          {isEditMode ? 'Update institute details' : 'Add an educational institute to your community'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Institute Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter institute name"
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe the institute, its programs, and specialties"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full address"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter city"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Phone number"
            />
          </div>

          <div>
            <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              id="contact_email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Email address"
            />
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="established_year" className="block text-sm font-medium text-gray-700 mb-2">
              Established Year
            </label>
            <input
              type="number"
              id="established_year"
              name="established_year"
              value={formData.established_year}
              onChange={handleInputChange}
              min="1900"
              max={new Date().getFullYear()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Year"
            />
          </div>

          <div>
            <label htmlFor="principal_name" className="block text-sm font-medium text-gray-700 mb-2">
              Principal/Head Name
            </label>
            <input
              type="text"
              id="principal_name"
              name="principal_name"
              value={formData.principal_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Principal name"
            />
          </div>

          <div>
            <label htmlFor="total_students" className="block text-sm font-medium text-gray-700 mb-2">
              Total Students
            </label>
            <input
              type="number"
              id="total_students"
              name="total_students"
              value={formData.total_students}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Number of students"
            />
          </div>
        </div>

        <div>
          <label htmlFor="facilities" className="block text-sm font-medium text-gray-700 mb-2">
            Facilities
          </label>
          <textarea
            id="facilities"
            name="facilities"
            value={formData.facilities}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="List available facilities (library, labs, sports, etc.)"
          />
        </div>

        <div>
          <label htmlFor="admission_info" className="block text-sm font-medium text-gray-700 mb-2">
            Admission Information
          </label>
          <textarea
            id="admission_info"
            name="admission_info"
            value={formData.admission_info}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Admission requirements, process, and deadlines"
          />
        </div>

        <SocietySearchableSelect
          societies={societies}
          value={formData.society}
          onChange={handleInputChange}
          loading={loadingSocieties}
          showCity={false}
        />

        {isEditMode && existingImages.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current images (set cover or remove)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {keptExistingImages.map((img, index) => {
                const isCover = displayImageIndex === index;
                return (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.url}
                      alt="Institute"
                      className={`w-full h-24 object-cover rounded-lg border-2 ${
                        isCover ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setDisplayImageIndex(index)}
                      disabled={loading}
                      className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                        isCover ? 'bg-blue-600 text-white' : 'bg-white/90 text-gray-700 hover:bg-white'
                      }`}
                    >
                      {isCover ? 'Cover' : 'Set cover'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setImagesToRemove((prev) => [...prev, img.id]);
                        remapDisplayIndexAfterRemove(index);
                      }}
                      disabled={loading}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 disabled:opacity-50"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ImageUpload
          selectedImages={selectedImages}
          onImagesChange={setSelectedImages}
          maxImages={Math.max(0, 5 - keptExistingImages.length)}
          disabled={loading}
          autoCompress
          displayImageIndex={displayImageIndex}
          onDisplayImageIndexChange={setDisplayImageIndex}
          imageIndexOffset={keptExistingImages.length}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_public"
            name="is_public"
            checked={formData.is_public}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
            Make this institute visible to all users (not just society members)
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-6">
          <button
            type="submit"
            disabled={loading || loadingSocieties}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <LoadingSpinner size="sm" /> : isEditMode ? 'Save Changes' : 'Add Institute'}
          </button>
          <button
            type="button"
            onClick={navigateBack}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default InstituteCreateForm;
